'use client';

import { useCallback, useEffect, useState } from 'react';
import { getActiveModel } from './api';
import { demoModel } from './demo';

export type ModelSource = 'generated' | 'offline';

export interface ModelConfidence {
  score: number;
  band: 'High' | 'Medium' | 'Low';
}

export interface ActiveModel {
  model: unknown;
  source: ModelSource;
  name: string;
  entityCount: number;
  /** Trust rollup (Step 9), when the model carries one. */
  confidence: ModelConfidence | null;
  /** Number of governance/quality findings on the model. */
  riskCount: number;
}

/** Pull the confidence rollup + risk count off a model, tolerant of older shapes. */
function readModelMeta(model: unknown): { confidence: ModelConfidence | null; riskCount: number } {
  const m = model as { meta?: { confidence?: ModelConfidence }; reporting_risks?: unknown[] };
  const c = m?.meta?.confidence;
  const confidence =
    c && typeof c.score === 'number' && (c.band === 'High' || c.band === 'Medium' || c.band === 'Low') ? c : null;
  return { confidence, riskCount: Array.isArray(m?.reporting_risks) ? m.reporting_risks.length : 0 };
}

interface State {
  active: ActiveModel | null;
  loading: boolean;
  error: string | null;
}

/**
 * Loads the model the dashboard queries (integration week). On mount it fetches
 * the tenant's **active** model. It never auto-generates one — a tenant with no
 * active model shows a clean "no model yet" state, so a tenant's catalog is never
 * polluted with an auto-created petstore model (its real model comes from the
 * connector's discovery). If the API is unreachable it falls back to the offline
 * demo model, clearly labelled, so the dashboard still renders.
 */
export function useActiveModel(): State & { regenerate: () => void } {
  const [state, setState] = useState<State>({ active: null, loading: true, error: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const stored = await getActiveModel();
      if (!stored) {
        setState({ active: null, loading: false, error: null }); // clean "no model yet"
        return;
      }
      const model = stored.model as { entities?: unknown[] };
      setState({
        active: {
          model: stored.model,
          source: 'generated',
          name: stored.sourceArtifacts[0] ?? 'model',
          entityCount: model.entities?.length ?? 0,
          ...readModelMeta(stored.model),
        },
        loading: false,
        error: null,
      });
    } catch (err) {
      // API unreachable → offline demo model so the dashboard still works.
      setState({
        active: {
          model: demoModel,
          source: 'offline',
          name: 'petstore.json (offline)',
          entityCount: demoModel.entities.length,
          ...readModelMeta(demoModel),
        },
        loading: false,
        error: (err as Error).message,
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { ...state, regenerate: () => void load() };
}
